import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import RecruiterLayout from "@/components/layout/RecruiterLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, TrendingUp, Trophy, Award, Clock, PlayCircle, ExternalLink, ClipboardList, Users } from "lucide-react";

export default function RecruiterPlayerProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/recruiter/players/${id}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Not found"));
  }, [id]);

  if (err) return <RecruiterLayout><div className="p-8"><Link to="/recruiter" className="text-xs uppercase tracking-wider text-[#E63946]">← Back to Search</Link><p className="mt-4 text-slate-600">{err}</p></div></RecruiterLayout>;
  if (!d) return <RecruiterLayout><div className="p-16 text-center label-overline">Loading profile…</div></RecruiterLayout>;

  const p = d.player;
  const r = d.rankings || {};
  const vm = d.verified_metrics || {};
  const featured = d.media.find((m) => m.is_featured) || d.media.find((m) => m.type === "video");

  return (
    <RecruiterLayout>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
          <Link to="/recruiter" data-testid="back-to-search" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /> Search</Link>
          <div className="flex flex-wrap items-center gap-6 mt-4">
            <div className="w-24 h-24 rounded-sm overflow-hidden bg-white/10 shrink-0">{p.profile_photo_url ? <img src={p.profile_photo_url} alt={p.first_name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><User className="w-10 h-10 text-white/40" /></div>}</div>
            <div className="min-w-0">
              <h1 data-testid="recruiter-profile-name" className="font-display text-4xl lg:text-5xl uppercase tracking-tighter">{p.first_name} {p.last_name}</h1>
              <p className="text-sm text-white/70 mt-1">{[p.primary_position, p.secondary_position, p.graduation_year && `Class of ${p.graduation_year}`, p.province, p.school].filter(Boolean).join(" · ")}</p>
            </div>
            {p.cbr_overall_rating != null && <div className="ml-auto text-center"><div className="font-data text-4xl">{Math.round(p.cbr_overall_rating * 100) / 100}</div><div className="label-overline text-white/50">CBR Rating</div></div>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Verified Metrics" icon={TrendingUp}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.keys(vm).length ? Object.entries(vm).map(([k, m]) => (
                <div key={k} className="border border-slate-100 rounded-sm p-3 text-center">
                  <div className="font-data text-2xl text-[#0A192F]">{m.value}<span className="text-xs text-slate-400"> {m.units}</span></div>
                  <div className="label-overline text-[9px] mt-1">{k.replace(/_/g, " ")}</div>
                </div>
              )) : <Empty text="No verified metrics yet." />}
            </div>
          </Card>

          <Card title="Scouting Evaluations" icon={ClipboardList}>
            {d.evaluations.length ? d.evaluations.map((e) => (
              <div key={e.id} className="border-b border-slate-100 last:border-0 py-3">
                <div className="flex items-center justify-between"><div className="font-semibold text-sm">{e.role_archetype || e.position_evaluated}</div>{e.overall_future_value && <span className="font-data text-lg text-[#E63946]">FV {e.overall_future_value}</span>}</div>
                <div className="text-xs text-slate-400">{e.evaluator_name} · {e.camp_name}</div>
                {e.written_report && <p className="text-sm text-slate-600 mt-1">{e.written_report}</p>}
              </div>
            )) : <Empty text="No published evaluations yet." />}
          </Card>

          <Card title="Career Timeline" icon={Clock}>
            {d.timeline.length ? <ul className="space-y-2">{d.timeline.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E63946] shrink-0" /><div><div className="font-semibold">{t.title}</div><div className="text-xs text-slate-400">{t.date ? new Date(t.date).toLocaleDateString("en-CA", { dateStyle: "medium" }) : ""}</div></div></li>
            ))}</ul> : <Empty text="No timeline events." />}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Rankings" icon={Trophy}>
            {r.rating != null ? <div className="space-y-1.5 text-sm">
              <RankRow label="National" v={r.national} t={r.national_total} />
              <RankRow label={`Provincial (${r.province || ""})`} v={r.provincial} t={r.provincial_total} />
              <RankRow label={`Position (${r.primary_position || ""})`} v={r.position} t={r.position_total} />
              <RankRow label={`Class of ${r.graduation_year || ""}`} v={r.graduation_year_rank} t={r.graduation_year_total} />
            </div> : <Empty text="Unranked." />}
          </Card>

          {featured && (
            <Card title="Featured Video" icon={PlayCircle}>
              {featured.embed_url || featured.url ? <a href={featured.url || featured.embed_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#E63946] font-semibold"><PlayCircle className="w-5 h-5" /> Watch {featured.title || "Highlight"}</a> : <Empty text="—" />}
            </Card>
          )}

          <Card title="Awards" icon={Award}>
            {d.awards.length ? <ul className="space-y-1.5">{d.awards.map((a) => <li key={a.id} className="text-sm flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#E63946] shrink-0" /><span className="truncate">{a.award_label}</span></li>)}</ul> : <Empty text="No awards yet." />}
          </Card>

          <Card title="Team History" icon={Users}>
            {d.team_history.length ? d.team_history.map((t) => <div key={t.team_id} className="text-sm border-b border-slate-100 last:border-0 py-1.5"><div className="font-semibold">{t.team_name}</div><div className="text-xs text-slate-400">{[t.season, t.division].filter(Boolean).join(" · ")}</div></div>) : <Empty text="No team history." />}
          </Card>

          <RecruitingLinks p={p} />
        </div>
      </div>
    </RecruiterLayout>
  );
}

function Card({ title, icon: Icon, children }) {
  return <div className="bg-white border border-slate-200 rounded-sm p-5"><div className="label-overline flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" /> {title}</div>{children}</div>;
}
function RankRow({ label, v, t }) {
  if (!v) return null;
  return <div className="flex items-center justify-between border-b border-slate-100 last:border-0 py-1"><span className="text-slate-500">{label}</span><span className="font-data text-[#0A192F]">#{v}<span className="text-slate-400 text-xs"> / {t}</span></span></div>;
}
function RecruitingLinks({ p }) {
  const links = [["FieldLevel", p.fieldlevel_url], ["NCSA", p.ncsa_url], ["Perfect Game", p.perfect_game_url], ["Prep Baseball", p.prep_baseball_url], ["Hudl", p.hudl_url], ["Website", p.personal_website_url], ["Instagram", p.instagram_handle && `https://instagram.com/${p.instagram_handle.replace("@", "")}`]].filter(([, u]) => u);
  if (!links.length) return null;
  return <div className="bg-white border border-slate-200 rounded-sm p-5"><div className="label-overline mb-3">Recruiting Links</div><div className="flex flex-wrap gap-2">{links.map(([l, u]) => <a key={l} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs border border-slate-300 px-2 py-1 rounded-sm hover:bg-slate-50">{l} <ExternalLink className="w-3 h-3" /></a>)}</div></div>;
}
function Empty({ text }) { return <p className="text-xs text-slate-400 py-1">{text}</p>; }
